import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Form, Input, Modal, Switch, Tooltip } from 'antd';
import { ClipboardList } from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  PAGINATION,
  createPolicySchema,
  isRankGroupKey,
  readableDate,
  updatePolicyVersionSchema,
  type Paginated,
  type PolicyDto,
  type PolicyVersionDto,
  type PolicyWithVersionDto,
  type PostTypeDto,
  type PostTypeSummaryDto,
  type RankGroupKey,
  type VehicleTypeDto,
  type VehicleTypeSummaryDto,
} from '@pams/shared';
import { PageHeader } from '@/components/PageHeader';
import { RequirementPreview } from '@/components/RequirementPreview';
import { TwoColumnLayout } from '@/components/TwoColumnLayout';
import { archivedAmong, columnCountLabel, resolvePolicyColumns, usableAmong } from '@/lib/policyColumns';
import { useAuth } from '@/hooks/useAuth';
import { can, mayAuthorPolicy, mayPublishPolicy, policyRefusal, actingOrgUnitTypeOf } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { applyServerIssues, useZodForm } from '@/lib/zodForm';
import { CommandRoleField, type CommandRoleRow } from './components/CommandRoleField';
import { CopyCommandRolesModal } from './components/CopyCommandRolesModal';
import { PostTypeOrderField } from './components/PostTypeOrderField';
import { PublishPolicyModal } from './components/PublishPolicyModal';
import { VehicleTypeField } from './components/VehicleTypeField';
import { VersionHistory } from './components/VersionHistory';
import { policyIsDirty } from './dirty';
import { policyPrefillOf } from './prefill';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE POLICY BUILDER — `TwoColumnLayout variant="form"`'s first real consumer.
 *
 *  Left: the fields. Right, sticky: A LIVE PREVIEW OF THE REQUIREMENT TABLE, exactly as
 *  a CP nodal will see it in P3. That preview is the point of the screen — someone
 *  defining a policy has no other way to tell whether they built the right thing, and a
 *  wrong policy propagates into every arrangement that uses it.
 *
 *  ─── HOW THE PREVIEW STAYS IN SYNC, AND THE TWO TRAPS ──────────────────────────
 *
 *  `Form.useWatch` WITH A SELECTOR, and both halves are load-bearing.
 *
 *  1. `useZodForm` calls `Form.useForm()` with no generic, so the instance is
 *     `FormInstance<any>` and the plain `useWatch('name', form)` overload resolves to
 *     `any` — an unsafe assignment under this repo's type-checked ruleset. The SELECTOR
 *     overload returns `TSelected`, so the `any` never escapes: it enters a parameter
 *     annotated `unknown`, which `no-unsafe-argument` permits. No change to the form
 *     bridge, no `FormInstance<T>` generic, no cast in a page.
 *
 *  2. ⚠️ rc-field-form decides whether to re-render by comparing `JSON.stringify` of the
 *     selected value, falling back to `Math.random()` when that throws. Return anything
 *     non-serialisable — a `safeParse` result whose failure arm carries a `ZodError` —
 *     and the preview re-renders on EVERY KEYSTROKE OF EVERY FIELD for ever, and nobody
 *     ever finds out why. `toDraft` therefore returns strings, booleans and arrays only.
 *
 *  NOT `onValuesChange`: it does not fire for programmatic writes (`setFieldValue`), and
 *  `pages/Places/components/PlaceDialog.tsx` — the file a developer would copy from —
 *  already uses one. NOT a second React state: two stores for one form is how a preview
 *  and a submit body come to disagree, which on this screen means publishing a version
 *  that does not match what the operator confirmed.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

type PolicyDraft = {
  name: string;
  description: string;
  postTypes: string[];
  vehicleTypes: string[];
  commandRoles: CommandRoleRow[];
};

const EMPTY_DRAFT: PolicyDraft = { name: '', description: '', postTypes: [], vehicleTypes: [], commandRoles: [] };

const asStrings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

const asRoles = (value: unknown): CommandRoleRow[] =>
  Array.isArray(value)
    ? value.flatMap((entry) => {
        if (typeof entry !== 'object' || entry === null) return [];
        const row = entry as {
          key?: unknown;
          label?: unknown;
          isRequired?: unknown;
          eligibleRankIds?: unknown;
          eligibleRankGroupKeys?: unknown;
        };
        return [
          {
            ...(typeof row.key === 'string' ? { key: row.key } : {}),
            label: typeof row.label === 'string' ? row.label : '',
            isRequired: row.isRequired === true,
            // [P4-UX.2] Absent reads as "any rank", which is the schema's default too.
            eligibleRankIds: Array.isArray(row.eligibleRankIds)
              ? row.eligibleRankIds.filter((id): id is string => typeof id === 'string')
              : [],
            /*
             * ⚠️ [C2b §5c] ABSENT READS AS "no groups", which is the schema's default and is
             * also §5f: an existing policy carries only ids, and nothing infers a group from
             * them. A form written before this pass round-trips unchanged.
             */
            eligibleRankGroupKeys: Array.isArray(row.eligibleRankGroupKeys)
              ? row.eligibleRankGroupKeys.filter((key): key is RankGroupKey => isRankGroupKey(String(key)))
              : [],
          },
        ];
      })
    : [];

/**
 * TOTAL, and returns only plain serialisable data — see trap 2 above. It never throws
 * and never returns `undefined`, which is what lets the caller's `??` fallback be about
 * first render rather than about error handling.
 */
const toDraft = (values: unknown): PolicyDraft => {
  if (typeof values !== 'object' || values === null) return EMPTY_DRAFT;
  const raw = values as Record<string, unknown>;
  return {
    name: typeof raw.name === 'string' ? raw.name : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    postTypes: asStrings(raw.postTypes),
    vehicleTypes: asStrings(raw.vehicleTypes),
    commandRoles: asRoles(raw.commandRoles),
  };
};

/**
 * ⚠️ [FIX-1c] THE COUNT STOPS LYING, AND DISCLOSES RATHER THAN HIDES.
 *
 * It read the raw array length, so a policy with five stored post types said "5 posts" while
 * only four columns rendered below it — the fifth had been archived. A bare "5" lies about
 * what is usable; a silent "4" hides that a column somebody remembers has gone. So:
 * "4 posts · 1 archived".
 *
 * `archivedOf` is a lookup against the MASTER rather than a second filter — the one that
 * decides usability is `resolvePolicyColumns`, and this counts the same fact from the same
 * `isActive` flag on the same summaries.
 */
const summaryLine = (
  draft: PolicyDraft,
  archived: { posts: number; vehicles: number },
  version?: PolicyVersionDto,
): string => {
  const usablePosts = draft.postTypes.length - archived.posts;
  const usableVehicles = draft.vehicleTypes.length - archived.vehicles;
  const parts = [
    columnCountLabel(usablePosts, archived.posts, 'post'),
    columnCountLabel(usableVehicles, archived.vehicles, 'vehicle type'),
    `${String(draft.commandRoles.length)} command role${draft.commandRoles.length === 1 ? '' : 's'}`,
  ];
  if (version) parts.push(`${version.status === 'DRAFT' ? 'Draft' : 'Published'} v${String(version.versionNumber)}`);
  return parts.join(' · ');
};

/**
 * [P4-UX] The two lock sentences. A page's own words live with the page.
 */
/**
 * ⚠️ [C3c §11a] WHY PUBLISH IS UNAVAILABLE, AND IT NAMES THE BUTTON THAT FIXES IT.
 *
 * A tooltip that only said "there are unsaved changes" states the problem and not the remedy;
 * this points at the control one place to the left, which is the whole of what the operator has
 * to do — [C1f §7b]'s distinction between a blocker an operator can act on and a refusal they
 * cannot.
 */
const POLICY_PUBLISH_BLOCKED = 'Save the draft first — publishing freezes the saved version, not what is on screen.';

const POLICY_LOCK_COPY = {
  published:
    'This version is published and cannot be changed. "Edit as new version" creates a draft copy to work on; the published version stays exactly as it is.',
} as const;

/**
 * ⚠️ [amended D4 §5c] `nameFixed` IS GONE, AND THAT IS THE REVERSAL [LIFECYCLE] NAMED.
 *
 * It told operators "a policy's name and description are fixed once it exists". That was
 * never true of the API — `PATCH /policies/:id` has updated both since P2 and simply had no
 * caller — so the screen was reporting a limitation the server did not have. This is the
 * caller, and the sentence below is what the old one should have said: renaming is allowed,
 * and it is retrospective.
 */
/**
 * ⚠️ [C2b §8e] THE WORDS FOR DISCARDING A DRAFT.
 *
 * It says what SURVIVES, which is the whole of what an operator is worried about: the published
 * version is untouched and every order bound to it renders exactly as it did. A dialog that says
 * only "are you sure?" makes them close it and leave the draft, which is how a policy ends up
 * with a stale draft nobody dares touch.
 */
const DISCARD_COPY = {
  action: 'Discard draft',
  title: (versionNumber: number): string => `Discard draft version ${String(versionNumber)}?`,
  body:
    'The draft and everything typed into it are deleted. The published version is untouched, and ' +
    'every order that uses this policy is unaffected. This cannot be undone.',
  ok: 'Discard the draft',
  done: 'Draft discarded',
} as const;

const RENAME_COPY = {
  action: 'Rename',
  title: 'Rename this policy',
  /**
   * ⚠️ THE OLD COPY'S REASON WAS RIGHT EVEN THOUGH ITS CONCLUSION WAS WRONG. A name resolves
   * at READ time, so a rename retitles every order already issued — which is usually what an
   * operator wants and is never what they expect. Said here, where the decision is made.
   */
  warning:
    'A policy’s name is read fresh every time an order is shown, so renaming it changes how every order that used it is labelled — including orders already issued.',
  nameLabel: 'Name',
  descriptionLabel: 'Description',
  required: 'A policy needs a name',
} as const;

const PolicyBuilderPage = () => {
  const { policyId } = useParams<{ policyId: string }>();
  const isNew = policyId === undefined;
  const { officer } = useAuth();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();

  /*
   * ⚠️ [C5c §3] THE CAPABILITY **AND** THE SEAT. `policy.write` and `policy.publish` list NODAL_ADMIN
   * now, and only one seated at the COMMISSIONERATE may author — `mayAuthorPolicy` /
   * `mayPublishPolicy`, the offer twins of the server's `assertMayAuthorPolicy`. A bare `can()` here
   * would open the whole builder to a divisional nodal admin whose every save 403s.
   *
   * ⚠️ AND THE DEFAULT IS A THIRD GATE: `policy.configure`, SUPER_ADMIN — the only configure act on
   * this screen, and the service refuses a create that asks for it without the key.
   */
  const canAuthor = officer ? mayAuthorPolicy(officer.accessRole, actingOrgUnitTypeOf(officer)) : false;
  const canPublish = officer ? mayPublishPolicy(officer.accessRole, actingOrgUnitTypeOf(officer)) : false;
  const canConfigure = officer ? can(officer.accessRole, 'policy.configure') : false;

  const [copying, setCopying] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameForm] = Form.useForm<{ name: string; description?: string }>();
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | undefined>(undefined);

  // The masters. Bounded whole-master reads — a picker over a dozen rows.
  const [postTypesQuery, vehicleTypesQuery] = useQueries({
    queries: [
      {
        queryKey: queryKeys.postTypes.options(),
        queryFn: () =>
          api.get<Paginated<PostTypeDto>>('/post-types', {
            query: { limit: PAGINATION.MAX_LIMIT, sortBy: 'displayOrder', sortOrder: 'asc' },
          }),
        staleTime: 5 * 60_000,
      },
      {
        queryKey: queryKeys.vehicleTypes.options(),
        queryFn: () =>
          api.get<Paginated<VehicleTypeDto>>('/vehicle-types', {
            query: { limit: PAGINATION.MAX_LIMIT, sortBy: 'displayOrder', sortOrder: 'asc' },
          }),
        staleTime: 5 * 60_000,
      },
    ],
  });
  // Memoised, because the `?? []` fallback is a fresh array on every render and these
  // feed the `useMemo`s that build the preview's summary maps.
  const postTypes = useMemo(() => postTypesQuery.data?.items ?? [], [postTypesQuery.data]);
  const vehicleTypes = useMemo(() => vehicleTypesQuery.data?.items ?? [], [vehicleTypesQuery.data]);

  const policyQuery = useQuery({
    queryKey: queryKeys.policies.detail(policyId ?? ''),
    queryFn: () => api.get<PolicyDto>(`/policies/${policyId ?? ''}`),
    enabled: !isNew,
  });
  const policy = policyQuery.data;

  /** The version this page EDITS: the open draft if there is one, else the published one. */
  const editableVersionId = policy?.draftVersionId ?? policy?.currentVersionId ?? null;

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [PRE-P6 §3b] `?version=` OPENS A PAST VERSION, READ-ONLY.
   *
   *  A QUERY PARAMETER on this route rather than a new one: the policy header, the masters
   *  and the preview are all identical, and a second page would be a second renderer of a
   *  policy version that would drift from this one. `pages/DutyFill`'s `?division=` set the
   *  precedent for narrowing a screen without changing its shape.
   *
   *  ⚠️ ABSENT MEANS "THE EDITABLE ONE", which keeps every existing `/policies/:id` link
   *  working unchanged and leaves no parameter behind after an edit.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const [searchParams] = useSearchParams();
  const requestedVersionId = searchParams.get('version');
  const viewingVersionId = requestedVersionId ?? editableVersionId;
  const isHistorical = requestedVersionId !== null && requestedVersionId !== editableVersionId;

  const versionQuery = useQuery({
    queryKey: queryKeys.policies.version(policyId ?? '', viewingVersionId ?? ''),
    queryFn: () => api.get<PolicyVersionDto>(`/policies/${policyId ?? ''}/versions/${viewingVersionId ?? ''}`),
    enabled: !isNew && viewingVersionId !== null,
  });
  const version = versionQuery.data;

  const isDraft = version?.status === 'DRAFT' || isNew;
  /*
   * ⚠️ [PRE-P6 §3b] `isHistorical` IS STATED EVEN THOUGH IT IS IMPLIED TODAY.
   *
   * A past version is always `PUBLISHED` — at most one DRAFT can exist per policy and it IS
   * the editable one — so `!isDraft` already makes this read-only. Naming the third reason
   * anyway is what stops a later change to `isDraft` quietly making a frozen record
   * editable, and it is the flag the actions below and the banner read.
   */
  const readOnly = !canAuthor || (!isNew && !isDraft) || isHistorical;

  const schema = isNew ? createPolicySchema : updatePolicyVersionSchema;
  const { form, field, submit, formErrors, setFormErrors } = useZodForm(schema);

  /*
   * ⚠️ [P7B §3c] A NEW POLICY MAY ARRIVE PREFILLED FROM THE TEMPLATE DECISIONS — columns and a name, by
   * URL. `null` without the parameters, and on an existing policy, so neither moves by a byte.
   */
  const prefill = useMemo(() => (isNew ? policyPrefillOf(searchParams) : null), [isNew, searchParams]);

  const initialValues = useMemo<PolicyDraft>(
    () => ({
      name: policy?.name ?? prefill?.name ?? '',
      description: policy?.description ?? '',
      postTypes: version?.postTypes.map((row) => row.postTypeId) ?? prefill?.postTypes ?? [],
      vehicleTypes: version?.vehicleTypes.map((row) => row.vehicleTypeId) ?? prefill?.vehicleTypes ?? [],
      commandRoles:
        version?.commandRoles.map((role) => ({
          key: role.key,
          label: role.label,
          isRequired: role.isRequired,
          eligibleRankIds: role.eligibleRankIds,
          /* ⚠️ [C2b §5c] The group half travels with the ids on every save. */
          eligibleRankGroupKeys: role.eligibleRankGroupKeys,
        })) ?? [],
    }),
    [policy, version, prefill],
  );

  /**
   * ⚠️ `initialValues` IS APPLIED ONCE, AT MOUNT — and the policy has not loaded yet.
   *
   * rc-field-form seeds the store from `initialValues` on the first render and never
   * looks at the prop again, so a form whose data arrives from a query is empty for ever
   * without this. The symptom is a preview that renders its empty state on a policy that
   * plainly has columns, which reads as a bug in the preview rather than in the form.
   *
   * Keyed on the memoised `initialValues`, so it fires when the loaded version changes
   * and not on every render. A refetch after a save deliberately DOES re-seed: what came
   * back from the server is the authoritative version at that point.
   */
  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  // See the header: the selector overload, and a total function returning plain data.
  const watched = Form.useWatch((values: unknown) => toDraft(values), form);
  const draft = watched ?? initialValues;
  /*
   * ⚠️ [C3c §11a] SAVE DRAFT → PUBLISH, ENFORCED. `publishPolicyVersion` freezes the STORED
   * draft, so an operator who edited and then pressed Publish shipped the version WITHOUT their
   * edit — silently, with the screen still showing it. There was no dirty tracking on this page
   * at all and Publish carried no `disabled`.
   *
   * ⚠️ BOTH SIDES THROUGH `toDraft`, so key order and filtering are identical by construction
   * and the first render cannot report a phantom edit. See `dirty.ts` for why this is driven by
   * `useWatch` rather than `onValuesChange` — this page writes PROGRAMMATICALLY at three sites,
   * and `onValuesChange` does not fire for those, so that gate would fail OPEN on exactly the
   * edits that matter most.
   */
  const dirty = policyIsDirty(draft, toDraft(initialValues));

  const postTypeSummaries = useMemo<ReadonlyMap<string, PostTypeSummaryDto>>(
    () =>
      new Map(
        postTypes.map((row) => [
          row.id,
          { id: row.id, name: row.name, shortName: row.shortName, archivedAt: row.archivedAt },
        ]),
      ),
    [postTypes],
  );
  const vehicleTypeSummaries = useMemo<ReadonlyMap<string, VehicleTypeSummaryDto>>(
    () =>
      new Map(
        vehicleTypes.map((row) => [
          row.id,
          { id: row.id, name: row.name, code: row.code, isWildcard: row.isWildcard, archivedAt: row.archivedAt },
        ]),
      ),
    [vehicleTypes],
  );

  /**
   * ⚠️ [FIX-1c/1d] How many of the DRAFT's chosen columns are archived in the master.
   *
   * Counted from the same `isActive` the resolution function reads, so the header and the
   * arrangement builder cannot disagree about which columns are usable.
   */
  /**
   * ⚠️ [FIX-1c] MERGED WITH THE VERSION'S OWN SUMMARIES, AND THAT IS THE WHOLE FIX.
   *
   * `/post-types` returns ACTIVE rows only — MEASURED: six exist, five come back. So the
   * master list alone can never see a archived column, and `archivedAmong` against it found
   * nothing while the header cheerfully said "5 posts". The saved version EMBEDS its own
   * summaries, archived ones included, so the two together know about every id the draft can
   * name: the master supplies what is choosable, the version supplies what was chosen.
   */
  const versionColumns = useMemo(() => resolvePolicyColumns(version), [version]);

  const knownPostTypes = useMemo(
    () => new Map([...versionColumns.allPostTypes, ...postTypeSummaries]),
    [versionColumns.allPostTypes, postTypeSummaries],
  );
  const knownVehicleTypes = useMemo(
    () => new Map([...versionColumns.allVehicleTypes, ...vehicleTypeSummaries]),
    [versionColumns.allVehicleTypes, vehicleTypeSummaries],
  );

  const archivedCounts = useMemo(
    () => ({
      posts: archivedAmong(draft.postTypes, knownPostTypes).length,
      vehicles: archivedAmong(draft.vehicleTypes, knownVehicleTypes).length,
    }),
    [draft.postTypes, draft.vehicleTypes, knownPostTypes, knownVehicleTypes],
  );

  /**
   * ⚠️ [FIX-1d] Edits the DRAFT's own selection. It never touches a published version.
   *
   * ⚠️ `knownPostTypes`, NOT `postTypeSummaries`, AND THE DIFFERENCE IS THE WHOLE BUTTON.
   * `usableAmong` keeps an id the map cannot resolve — deliberately, since "unknown" is not
   * "archived". The master list returns ACTIVE ROWS ONLY, so against it a archived id is
   * ABSENT rather than inactive, and every id survived: the button appeared, said how many
   * there were, and removed nothing. The merged map is the one that can actually see them.
   */
  const dropArchivedColumns = (): void => {
    form.setFieldValue('postTypes', usableAmong(draft.postTypes, knownPostTypes));
    form.setFieldValue('vehicleTypes', usableAmong(draft.vehicleTypes, knownVehicleTypes));
  };

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });

  /*
   * ⚠️ [C2b §8e] IT NAVIGATES TO THE LIST RATHER THAN STAYING. The version this page is
   * editing no longer exists, so remaining here would refetch a 404 and render the read-only
   * banner over an empty form — `duplicateArrangement`'s rule that a control which destroys
   * what the page is showing must take the operator somewhere that still exists.
   */
  const discardDraft = useMutation({
    mutationFn: () => api.delete(`/policies/${policyId ?? ''}/versions/${version?.id ?? ''}`),
    onSuccess: () => {
      invalidate();
      void message.success(DISCARD_COPY.done);
      void navigate('/policies');
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const save = useMutation<PolicyWithVersionDto | PolicyVersionDto, ApiError, Record<string, unknown>>({
    mutationFn: (values) => {
      return isNew
        ? api.post<PolicyWithVersionDto>('/policies', values)
        : api.patch<PolicyVersionDto>(`/policies/${policyId ?? ''}/versions/${version?.id ?? ''}`, values);
    },

    onSuccess: (result) => {
      invalidate();

      if ('policy' in result) {
        void message.success('Policy created');
        void navigate(`/policies/${result.policy.id}`, { replace: true });
        return;
      }

      void message.success(`Draft v${String(result.versionNumber)} saved`);
    },

    onError: (error) => {
      setFormErrors(applyServerIssues(form, error.details).concat(Array.isArray(error.details) ? [] : [error.message]));
    },
  });

  const publish = useMutation({
    mutationFn: () => api.post<PolicyVersionDto>(`/policies/${policyId ?? ''}/versions/${version?.id ?? ''}/publish`),

    onSuccess: (published) => {
      void message.success(`Version ${String(published.versionNumber)} published`);
      setPublishing(false);
      invalidate();
    },

    onError: (error: ApiError) => {
      setPublishError(error.message);
    },
  });

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended C2a §2c] THE SAVE-THEN-PUBLISH CHAIN IS GONE, AND WITH IT THE EMPTY `catch`.
   *
   *  §2c named an empty `catch {}` here — "a confirm button that silently does nothing looks
   *  exactly like data loss to an operator". It did, and improving the catch was the wrong fix:
   *  MEASURED against `origin/main`, this page had no `handlePublish` at all. `onConfirm` was
   *  `() => publish.mutate()`. `fc1a786` inserted a `save.mutateAsync()` ahead of the publish,
   *  wrapped the pair in the empty catch, and **broke both publish tests** —
   *  `does not publish until the confirmation is accepted` and `shows a rejected publish INSIDE
   *  the modal` have been red ever since, because a save that rejects skips the publish and the
   *  catch ate the reason.
   *
   *  ⚠️ AND IT IS THE SAME SHAPE AS §2's ARRANGEMENT-SIDE DEFECT, WHICH IS WHY IT IS REMOVED
   *  RATHER THAN REPAIRED. An unrequested PATCH on the publish path invalidates and re-seeds
   *  the form from the server's own copy — [P3b.3]'s loss exactly, and the reason
   *  `saveKeyIsolation.test.ts` exists one directory over. Publishing should publish.
   *
   *  ⚠️ THE HAZARD IT WAS REACHING FOR IS REAL AND IS ALREADY ANSWERED. `publishPolicyVersion`
   *  freezes the STORED draft, so unsaved edits are not published — which is what `Save draft`
   *  is for, sitting beside this button, exactly as it did before the branch.
   * ─────────────────────────────────────────────────────────────────────────────────
   */

  const left = (
    <Form
      form={form}
      layout="vertical"
      disabled={readOnly}
      initialValues={initialValues}
      onFinish={(values: Record<string, unknown>) => {
        const parsed = submit(values);
        if (parsed.ok) save.mutate(parsed.data);
      }}
      className="flex flex-col gap-4"
    >
      {formErrors.length > 0 && <Alert type="error" showIcon message={formErrors.join(' ')} />}

      {/*
        ⚠️ [PRE-P6 §3b] A PAST VERSION SAYS SO, AND SAYS HOW TO LEAVE.
        Every control below is already disabled, but a form full of greyed fields with no
        sentence explaining why reads as a permissions failure — which is the one thing an
        operator will report as a bug. The way back is a real link, `NameCell`'s rule.
      */}
      {/*
        [P7B §3c] A policy started from the template decisions says where to go back to — once it is
        published, the decisions page offers it for every column set it covers.
      */}
      {prefill?.templatesSetKey ? (
        <Alert
          type="info"
          showIcon
          data-testid="policy-from-templates"
          message="These columns come from the template decisions."
          description={
            <span>
              Save and publish the policy, then{' '}
              <Link
                className="text-link underline-offset-2 hover:underline"
                to={`/templates/${prefill.templatesSetKey}/decisions?class=POLICY`}
              >
                go back to the policy decisions
              </Link>{' '}
              to choose it.
            </span>
          }
        />
      ) : null}

      {isHistorical && version && (
        <Alert
          type="info"
          showIcon
          data-testid="viewing-past-version"
          message={`Viewing version ${String(version.versionNumber)}${
            version.publishedAt ? `, published ${readableDate(version.publishedAt) ?? ''}` : ''
          }.`}
          description={
            <span>
              A published version is frozen and cannot be edited — orders raised against it render from it for ever.{' '}
              <Link className="text-link underline-offset-2 hover:underline" to={`/policies/${policyId ?? ''}`}>
                Open the current version
              </Link>
              .
            </span>
          }
        />
      )}

      {/*
        ⚠️ [P4-UX] SAY IT ONCE, AND SAY WHICH LOCK IT IS. THERE ARE TWO, WEARING ONE GREY.

        Name and Description are `disabled={!isNew || readOnly}` — so they are locked on ANY
        existing policy, INCLUDING an open draft — and nothing on the page said so. Measured
        in dark mode, which is what the product ships in: a disabled input renders
        `rgba(255,255,255,0.04)` over the `#141414` card, which is LIGHTER than the enabled
        `rgb(20,20,20)` beside it. The only reliable cue was the text alpha, and typing into
        a field that looks live and silently rejects you reads as a broken page.

        Two sentences because the two locks have different answers: a published version is
        frozen and the way forward is a new one; a draft's name is fixed because arrangements
        already refer to it, and everything else on the page is still editable.
      */}
      {/*
        ⚠️ [FIX-1d] A DRAFT OFFERS TO DROP A ARCHIVED COLUMN. A PUBLISHED VERSION NEVER DOES.
        ────────────────────────────────────────────────────────────────────────────────
        A draft is still editable, and carrying a dead column into a publish is a defect in
        waiting: it would be frozen there for ever and every arrangement raised against it
        would have to filter it out at read time.

        ⚠️ It is an OFFER, not an automatic removal. A archived post type is sometimes
        archived by mistake, and silently editing somebody's draft while they look at it is
        how a form loses trust. The button says exactly how many and does nothing until it
        is pressed.
      */}
      {isDraft && !readOnly && archivedCounts.posts + archivedCounts.vehicles > 0 && (
        <Alert
          type="warning"
          showIcon
          data-testid="draft-archived-columns"
          message={`${String(archivedCounts.posts + archivedCounts.vehicles)} column(s) on this draft have been archived in the master. They cannot be used to raise an arrangement.`}
          action={
            <Button size="small" onClick={dropArchivedColumns}>
              Remove them
            </Button>
          }
        />
      )}

      {/*
        ⚠️ `published` STAYS. A published VERSION is still frozen, and conflating "this
        version cannot change" with "this policy cannot be renamed" is exactly what the two
        constants exist to keep apart.
      */}
      {!isNew && !isDraft && <Alert type="info" showIcon message={POLICY_LOCK_COPY.published} />}

      <Modal
        open={renameOpen}
        title={RENAME_COPY.title}
        okText={RENAME_COPY.action}
        onCancel={() => {
          /* ⚠️ THE RESET BELONGS TO THE DISMISSAL HANDLER TOO, AND UNCONDITIONALLY — the
             rc-field-form store outlives the subtree that displayed it (CLAUDE.md §8). */
          renameForm.resetFields();
          setRenameOpen(false);
        }}
        onOk={() => {
          void renameForm.validateFields().then((values) =>
            api
              .patch(`/policies/${policyId ?? ''}`, { name: values.name, description: values.description ?? null })
              .then(() => {
                void message.success('Policy renamed');
                renameForm.resetFields();
                setRenameOpen(false);
                invalidate();
              })
              .catch((error: ApiError) => void message.error(error.message)),
          );
        }}
      >
        <Alert type="warning" showIcon className="mb-3" message={RENAME_COPY.warning} />
        <Form form={renameForm} layout="vertical">
          <Form.Item
            name="name"
            label={RENAME_COPY.nameLabel}
            rules={[{ required: true, whitespace: true, message: RENAME_COPY.required }]}
          >
            <Input maxLength={120} data-testid="rename-policy-name" />
          </Form.Item>
          <Form.Item name="description" label={RENAME_COPY.descriptionLabel}>
            <Input.TextArea rows={2} maxLength={1_000} />
          </Form.Item>
        </Form>
      </Modal>

      {/*
        ⚠️ [LIFECYCLE] THE HIDDEN BINDING, AND WITHOUT IT "COPY COMMAND ROLES" COPIED NOTHING.

        `CopyCommandRolesModal` wrote this key with `form.setFieldValue` and no `Form.Item`
        ever declared it. rc-field-form builds `validateFields()`'s name list from REGISTERED
        FIELD ENTITIES ONLY, and `onFinish` receives `getFieldsValue(namePathList)` — so an
        unregistered key is invisible to the submit, never reaches `zodSubmit`, and never
        reaches the payload. The screen said "Command roles will be copied when the policy is
        created" and then created a policy with none, because the modal ALSO cleared the roles.

        This is CLAUDE.md §8's [P3a.1] `Form.useWatch` trap wearing different clothes — the
        same registered-fields-only rule, applied to `onFinish` rather than to a selector.
        `pages/ArrangementBuilder` binds `requirementLines` and `commandEntries` exactly this
        way, for exactly this reason.
      */}
      <Form.Item name={['copyCommandRolesFromPolicyId']} noStyle>
        <input type="hidden" />
      </Form.Item>

      <Card size="small" title="Policy">
        <Form.Item {...field('name')} label="Name">
          <Input placeholder="Standard PA" disabled={!isNew || readOnly} />
        </Form.Item>
        <Form.Item {...field('description')} label="Description">
          <Input.TextArea
            rows={2}
            placeholder="What kind of arrangement this policy is for."
            disabled={!isNew || readOnly}
          />
        </Form.Item>
        {/* ⚠️ [C5c §3] `policy.configure` — an HQ nodal admin creates a policy, a super admin makes
            one the default. Absent rather than disabled: the `org.reparent` rule for an act this
            role structurally cannot take, and the server refuses the body regardless. */}
        {isNew && canConfigure && (
          <Form.Item
            name={['isDefault']}
            label="Default policy"
            valuePropName="checked"
            help="Setting this clears whichever policy is default now."
          >
            <Switch />
          </Form.Item>
        )}
      </Card>

      <Card
        size="small"
        title="Post columns"
        extra={<span className="text-xs text-muted-foreground">Order is the grid order</span>}
      >
        <p className="mb-2 text-xs text-muted-foreground">
          The force this arrangement asks for. Each one becomes a column a CP nodal enters a total against — so pick the
          posts that get COUNTED, and leave command to the card below.
        </p>
        <Form.Item {...field('postTypes')} noStyle>
          {/* ⚠️ [D4.5 §1b] `knownPostTypes` merges the master with the version's own embedded
              summaries, so an ARCHIVED column keeps its name here instead of vanishing from
              the ordered list — which used to shift the order and then write it back. */}
          <PostTypeOrderField postTypes={postTypes} resolve={(id) => knownPostTypes.get(id)} disabled={readOnly} />
        </Form.Item>
      </Card>

      <Card size="small" title="Vehicles">
        <Form.Item {...field('vehicleTypes')} noStyle>
          <VehicleTypeField
            vehicleTypes={vehicleTypes}
            resolve={(id) => knownVehicleTypes.get(id)}
            disabled={readOnly}
          />
        </Form.Item>
      </Card>

      <Card
        size="small"
        title="Command roles"
        extra={
          isNew ? (
            <Button size="small" disabled={readOnly} onClick={() => setCopying(true)}>
              Copy from existing policy…
            </Button>
          ) : undefined
        }
      >
        <p className="mb-2 text-xs text-muted-foreground">
          Who commands this arrangement — Supervision, In Charge, Assisting Inspector. They are filled with named
          officers and are <strong>never counted</strong> in a total, an allocation or a shortfall, so they need no post
          type and never appear as a column.
        </p>
        <Form.Item {...field('commandRoles')} noStyle>
          <CommandRoleField disabled={readOnly} />
        </Form.Item>
      </Card>

      {/* ⚠️ [PRE-P6 §3b] LAST, and only for a policy that exists: a NEW one has no history,
          and putting an empty card above the fields would be the first thing an operator
          creating a policy read. */}
      {!isNew && policyId && (
        <VersionHistory policyId={policyId} viewingVersionId={viewingVersionId} editableVersionId={editableVersionId} />
      )}
    </Form>
  );

  const right =
    (
      /**
       * ⚠️ `max-h` + `overflow-y-auto` ON THE STICKY WRAPPER.
       *
       * A sticky element taller than the viewport is unreachable at its bottom: sticky
       * elements do not scroll internally, and `<main>` has already scrolled past it. This
       * is a PAGE-level fix rather than a change to `TwoColumnLayout`, whose doc comment
       * now records the trap — P3's order preview will meet it on day one, and the symptom
       * ("the bottom of the preview cannot be reached") does not point at `position:
       * sticky` on its own.
       */
      <aside aria-label="Requirement preview">
        <RequirementPreview
          postTypeIds={draft.postTypes}
          postTypes={postTypeSummaries}
          vehicleTypeIds={draft.vehicleTypes}
          vehicleTypes={vehicleTypeSummaries}
          commandRoles={draft.commandRoles}
        />
      </aside>
    );

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={ClipboardList}
        parent={{ label: 'Policies', to: '/policies' }}
        title={isNew ? 'New policy' : (policy?.name ?? 'Policy')}
        // Useful at every width, and the mitigation for the preview sitting below the
        // whole form at mobile — where source order puts it, correctly.
        subtitle={summaryLine(draft, archivedCounts, version)}
        actions={
          <div className="flex items-center gap-2">
            {/*
              ⚠️ [D4 §5c / LIFECYCLE gap 1] THE CALLER `PATCH /policies/:id` NEVER HAD.
              A controlled `<Modal>` with a required, validated input — never
              `modal.confirm`, which cannot carry one (CLAUDE.md §8, `PublishPolicyModal`'s
              precedent).
            */}
            {/* ⚠️ [PRE-P6 §3b] Hidden on a past version. Renaming the POLICY from a frozen
                version's page is legal and incoherent — the header above says "Viewing
                version 1" and the action would change something version 1 does not own. */}
            {!isNew && canAuthor && !isHistorical && (
              <Button
                data-testid="rename-policy"
                onClick={() => {
                  renameForm.setFieldsValue({ name: policy?.name ?? '', description: policy?.description ?? '' });
                  setRenameOpen(true);
                }}
              >
                {RENAME_COPY.action}
              </Button>
            )}
            {isDraft && (
              <Tooltip title={canAuthor ? undefined : policyRefusal(officer?.accessRole ?? 'NONE')}>
                <Button disabled={readOnly} loading={save.isPending} onClick={() => form.submit()}>
                  {isNew ? 'Create policy' : 'Save draft'}
                </Button>
              </Tooltip>
            )}
            {/* ⚠️ [PRE-P6 §3b] `!isHistorical`, so a fork is always taken from the CURRENT
                version. Forking v1 while v3 is published would create v4 from a two-edit-old
                shape, which is a real thing to want and not a thing to offer by accident
                from a page whose banner says "read-only". */}
            {!isNew && !isDraft && canAuthor && !isHistorical && (
              // A published version is immutable. The way forward is a new version, and
              // saying so is better than a disabled Save nobody can interpret.
              <Button
                onClick={() => {
                  void api
                    .post<PolicyVersionDto>(`/policies/${policyId ?? ''}/versions`, { fromVersionId: version?.id })
                    .then(() => {
                      void message.success('New draft created');
                      invalidate();
                    })
                    .catch((error: ApiError) => void message.error(error.message));
                }}
              >
                Edit as new version
              </Button>
            )}
            {/*
              ─── ⚠️ [C2b §8e] DISCARD A DRAFT. THE SERVER HAS ALWAYS ALLOWED THIS ────────────
              `DELETE /policies/:id/versions/:versionId` has existed since [P2] and nothing in
              the product called it — so an operator who opened a draft by mistake had no way
              back except to publish it or leave it sitting there for ever, which is the state
              `syncPolicyPointers` then has to reason about on every read.

              ⚠️ NEVER OFFERED ON A PUBLISHED VERSION, and the server refuses one anyway. That
              refusal is the control; this is the offer gate, and hiding a control that always
              409s is the difference between a screen that is honest and one that argues.

              ⚠️ AND IT IS `policy.write`, NOT `policy.publish`. Discarding a draft unmakes an
              edit; publishing freezes one for ever. [P2] split those two capabilities on
              exactly that argument and this is the first thing to use the narrower half.

              ⚠️ [amended C5c §3] THROUGH `mayAuthorPolicy` NOW, so an HQ nodal admin may discard —
              §3a names it — and a divisional one may not. Still unaudited, as §3b's "unchanged"
              keeps; `deletePolicyVersion`'s header states that cost.

              ⚠️ `modal.confirm` IS CORRECT HERE and `PublishPolicyModal` is not the precedent:
              that one needs a required, validated INPUT, which `confirm` cannot carry. This
              asks one irreversible yes/no.
            */}
            {!isNew && isDraft && canAuthor && !isHistorical && version && (
              <Button
                danger
                data-testid="discard-draft"
                loading={discardDraft.isPending}
                onClick={() => {
                  modal.confirm({
                    title: DISCARD_COPY.title(version.versionNumber),
                    content: <span className="text-sm">{DISCARD_COPY.body}</span>,
                    okText: DISCARD_COPY.ok,
                    okButtonProps: { danger: true },
                    onOk: () => {
                      discardDraft.mutate();
                    },
                  });
                }}
              >
                {DISCARD_COPY.action}
              </Button>
            )}
            {/* ABSENT rather than disabled for a role that structurally cannot publish —
                the `org.reparent` rule from OrgUnitDetail. */}
            {!isNew && isDraft && canPublish && version && (
              /*
                ⚠️ [C3c §11a] `disabled` WHILE THERE IS SOMETHING UNSAVED, AND PLAIN `disabled`
                RATHER THAN [C1f §7b]'s CLICKABLE-BUT-UNAVAILABLE TREATMENT.

                That treatment exists because "a blocker is a to-do list the operator can act
                on" and the list is behind the control. Here the action is ONE CLICK ON THE
                BUTTON IMMEDIATELY TO THE LEFT, so a disabled button plus a tooltip naming it is
                the shorter path — and there is nothing to open.

                ⚠️ AND SAVE DRAFT KEEPS NO DIRTY CONDITION. A disabled Save on an unedited draft
                is a control that looks broken, and a redundant PATCH is harmless. The two
                buttons differ deliberately.
              */
              <Tooltip title={dirty ? POLICY_PUBLISH_BLOCKED : undefined}>
                <Button
                  type="primary"
                  disabled={dirty}
                  data-testid="publish-version"
                  onClick={() => {
                    setPublishError(undefined);
                    setPublishing(true);
                  }}
                >
                  Publish version {version.versionNumber}…
                </Button>
              </Tooltip>
            )}
          </div>
        }
      />

      <TwoColumnLayout variant="form" left={left} right={right} secondaryLabel="Requirement preview" />

      <CopyCommandRolesModal
        open={copying}
        {...(policyId ? { excludePolicyId: policyId } : {})}
        onClose={() => setCopying(false)}
        onPick={(sourcePolicyId) => {
          setCopying(false);
          // Server-side: only the id travels, so the KEYS are copied rather than
          // re-minted from labels. See the modal's header.
          form.setFieldValue('copyCommandRolesFromPolicyId', sourcePolicyId);
          form.setFieldValue('commandRoles', []);
          void message.info('Command roles will be copied when the policy is created.');
        }}
      />

      {version && (
        <PublishPolicyModal
          open={publishing}
          policyName={policy?.name ?? ''}
          versionNumber={version.versionNumber}
          becomesDefault={policy?.isDefault ?? false}
          postTypeIds={draft.postTypes}
          postTypes={postTypeSummaries}
          vehicleTypeIds={draft.vehicleTypes}
          vehicleTypes={vehicleTypeSummaries}
          commandRoles={draft.commandRoles}
          {...(publishError ? { error: publishError } : {})}
          publishing={publish.isPending}
          onClose={() => setPublishing(false)}
          onConfirm={() => publish.mutate()}
        />
      )}
    </div>
  );
};

export default PolicyBuilderPage;
